import { useState, useEffect } from 'react';
import { MessageCircle, Send, Pencil, Trash2, X, Check } from 'lucide-react';
import { api } from '@/shared/services/api';
import toast from 'react-hot-toast';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { cn } from '@/shared/utils/cn';
import { getInitials } from '@/shared/utils/user';
import { formatRelative, formatDateTime } from '@/shared/utils/date';

interface Comment {
  id: number;
  feedItemId: number;
  userId: string;
  content: string;
  createdAt: string;
  updatedAt?: string;
  isEdited: boolean;
  authorName: string | null;
}

interface CommentsProps {
  feedItemId: number;
  currentUserId: string;
  /** Admins may edit or delete any comment, not just their own (SC-293). */
  isAdmin?: boolean;
}

/** Grow a textarea to fit its content. */
const autoGrow = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

export function Comments({ feedItemId, currentUserId, isAdmin = false }: CommentsProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadComments = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/comments/feeditem/${feedItemId}`);
        const commentsData = response.data;
        setComments(commentsData);
      } catch (error) {
        toast.error('Failed to load comments');
        console.error('Error loading comments:', error);
      } finally {
        setLoading(false);
      }
    };

    loadComments();
  }, [feedItemId]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      const response = await api.post('/comments', {
        feedItemId,
        content: newComment.trim(),
      });

      const newCommentData = response.data;
      setComments([newCommentData, ...comments]);

      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
      console.error('Error adding comment:', error);
    }
  };

  const handleUpdateComment = async (commentId: number) => {
    if (!editContent.trim()) return;

    try {
      const response = await api.put(`/comments/${commentId}`, {
        content: editContent.trim(),
      });

      setComments(
        comments.map((c) => (c.id === commentId ? response.data : c))
      );
      setEditingId(null);
      setEditContent('');
      toast.success('Comment updated');
    } catch (error) {
      toast.error('Failed to update comment');
      console.error('Error updating comment:', error);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!confirm('Are you sure you want to delete this comment?')) return;

    try {
      await api.delete(`/comments/${commentId}`);
      setComments(comments.filter((c) => c.id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
      console.error('Error deleting comment:', error);
    }
  };

  const startEditing = (comment: Comment) => {
    setEditingId(comment.id);
    setEditContent(comment.content);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditContent('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
        <Spinner />
        <span className="text-sm">Loading comments…</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 py-4 animate-fade-in">
      {/* Section header */}
      <div className="flex items-center gap-2">
        <MessageCircle className="h-5 w-5 text-primary" aria-hidden="true" />
        <h3 className="text-lg font-semibold text-foreground">Comments</h3>
        {comments.length > 0 && (
          <span className="badge-neutral">{comments.length}</span>
        )}
      </div>

      {/* Pinned composer */}
      <div className="card p-4">
        <textarea
          className="input min-h-[80px] resize-none"
          rows={3}
          placeholder="Add a comment…"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onInput={(e) => autoGrow(e.currentTarget)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              handleAddComment();
            }
          }}
        />
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            <kbd className="rounded border border-border px-1 py-0.5 font-sans text-[10px]">⌘</kbd>
            {' + '}
            <kbd className="rounded border border-border px-1 py-0.5 font-sans text-[10px]">Enter</kbd>
            {' to post'}
          </p>
          <button
            onClick={handleAddComment}
            disabled={!newComment.trim()}
            className="btn-primary btn-sm"
          >
            <Send className="h-4 w-4" aria-hidden="true" />
            Post Comment
          </button>
        </div>
      </div>

      {/* Activity stream */}
      {comments.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={MessageCircle}
            title="No comments yet"
            description="Be the first to share your thoughts on this item."
          />
        </div>
      ) : (
        <ul className="space-y-3">
          {comments.map((comment) => {
            const authorName = comment.authorName ?? 'Unknown user';
            const authorUnknown = !comment.authorName || comment.authorName === 'Unknown user';
            const isOwner = comment.userId === currentUserId;
            const canModify = isOwner || isAdmin;
            const isEditing = editingId === comment.id;
            const created = new Date(comment.createdAt);

            return (
              <li key={comment.id} className="card p-4">
                <div className="flex gap-3">
                  {/* Avatar */}
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
                    aria-hidden="true"
                  >
                    {getInitials(authorName)}
                  </div>

                  <div className="min-w-0 flex-1">
                    {/* Header row */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p
                          className={cn(
                            'truncate text-sm font-medium text-foreground',
                            authorUnknown && 'italic text-muted-foreground'
                          )}
                        >
                          {authorName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          <time dateTime={created.toISOString()} title={formatDateTime(comment.createdAt)}>
                            {formatRelative(comment.createdAt)}
                          </time>
                          {comment.isEdited && <span className="ml-1.5 italic">(edited)</span>}
                        </p>
                      </div>

                      {canModify && !isEditing && (
                        <div className="flex shrink-0 gap-1">
                          <button
                            onClick={() => startEditing(comment)}
                            className="btn-icon btn-ghost btn-sm"
                            title="Edit comment"
                            aria-label="Edit comment"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="btn-icon btn-ghost btn-sm text-danger hover:bg-danger-subtle"
                            title="Delete comment"
                            aria-label="Delete comment"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Body */}
                    {isEditing ? (
                      <div className="mt-3 space-y-2">
                        <textarea
                          className="input min-h-[72px] resize-none"
                          rows={3}
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          onInput={(e) => autoGrow(e.currentTarget)}
                          onKeyDown={(e) => {
                            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                              e.preventDefault();
                              handleUpdateComment(comment.id);
                            }
                          }}
                        />
                        <div className="flex justify-end gap-2">
                          <button onClick={cancelEditing} className="btn-ghost btn-sm">
                            <X className="h-4 w-4" aria-hidden="true" />
                            Cancel
                          </button>
                          <button
                            onClick={() => handleUpdateComment(comment.id)}
                            disabled={!editContent.trim()}
                            className="btn-primary btn-sm"
                          >
                            <Check className="h-4 w-4" aria-hidden="true" />
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                        {comment.content}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
